import { useEffect } from 'react'
import { Alert, DatePicker, Form, Input, Modal, Select } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { tenantToday } from '@/shared/lib/dayRange'
import { type Money } from '@/shared/lib/money'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { REASON_MAX } from './hrApi'
import { addPayEvent, type PayComponent } from './payApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

const DATE_FORMAT = 'YYYY-MM-DD'

interface Props {
  open: boolean
  personId: string
  /** ⚠️ FAQAT bir martalik (`recurring === false`) va faol qismlar */
  components: readonly PayComponent[]
  onClose: () => void
}

interface FormValues {
  componentId: string
  amount: Money | null
  occurredOn: Dayjs
  note?: string
}

/**
 * Bir martalik to'lov — mukofot, avans, jarima.
 *
 * <h2>⚠️ SANA — QAYSI VEDOMOSTGA TUSHISHINI BELGILAYDI</h2>
 * `occurredOn` hodisa sanasi, kiritilgan sana EMAS. Vedomost aynan shu
 * sana bo'yicha yig'adi, ya'ni iyul mukofotini avgustda kiritsangiz
 * ham u IYUL vedomostiga tushadi. Shuning uchun maydon oldindan
 * to'ldirilgan bo'lsa ham majburiy va izohi bor.
 *
 * <h2>⚠️ VEDOMOSTGA TUSHGAN HODISA O'ZGARMAYDI</h2>
 * `settled = true` bo'lgach uni tahrirlash yo'li YO'Q — backendda
 * ham bunday endpoint yo'q. Tuzatish yo'li: vedomostni bekor qilish
 * (hodisalar bo'shatiladi) yoki teskari ishorali yangi hodisa.
 *
 * <h2>⚠️ TANLOVDA FAQAT BIR MARTALIK QISMLAR</h2>
 * `hr.pay_event_guard()` takrorlanadigan qismga hodisa yozishni rad
 * etadi — sabab `SalaryItemModal` dagi bilan bir xil, faqat teskari
 * tomonga.
 */
export function PayEventModal({ open, personId, components, onClose }: Props) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      componentId: undefined,
      amount: null,
      occurredOn: tenantToday(),
      note: '',
    })
  }, [open, form])

  const add = useMutation({
    mutationFn: (values: FormValues) =>
      addPayEvent(personId, {
        componentId: values.componentId,
        occurredOn: values.occurredOn.format(DATE_FORMAT),
        amountMinor: values.amount?.amount ?? 0,
        note: values.note?.trim() ? values.note.trim() : null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
      form.resetFields()
      onClose()
    },
  })

  const close = () => {
    add.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('hr.payEvent.addTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => add.mutate(values))}
      okButtonProps={{ disabled: components.length === 0 }}
      confirmLoading={add.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      <div style={{ marginBottom: 16 }}>
        <ExpandableHint text={t('hr.payEvent.note')} fontSize={13} />
      </div>

      {add.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(add.error, t)}
        />
      ) : null}

      {components.length === 0 ? (
        <Alert type="warning" showIcon message={t('hr.payEvent.noComponents')} />
      ) : (
        <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={add.isPending}>
          <Form.Item
            name="componentId"
            label={t('hr.salary.component')}
            rules={[{ required: true, message: t('hr.salary.validation.componentRequired') }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('hr.salary.componentPlaceholder')}
              options={components.map((component) => ({
                value: component.id,
                label: `${seededName('payComponents', component.code, component.name, t)} · ${t(`payComponentKind.${component.kind}`)}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="amount"
            label={t('hr.salary.amount')}
            extra={t('hr.salary.amountHint')}
            rules={[
              {
                validator: (_rule, value: Money | null) =>
                  value && value.amount > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('hr.salary.validation.amountRequired'))),
              },
            ]}
          >
            <MoneyInput autoFocus />
          </Form.Item>

          <Form.Item
            name="occurredOn"
            label={t('hr.payEvent.occurredOn')}
            extra={t('hr.payEvent.occurredOnHint')}
            rules={[{ required: true, message: t('hr.payEvent.validation.occurredOnRequired') }]}
          >
            <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="note"
            label={t('hr.payEvent.noteField')}
            rules={[{ max: REASON_MAX, message: t('hr.assignment.validation.reasonLength') }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
